import { get, omit, pick, pickBy } from 'lodash-es';
import { getSearchResultsQuery } from 'site-modules/shared/utils/inventory/params-conversion';
import { getPagePublicationState } from 'site-modules/shared/utils/inventory/srp-utils';
import {
  BODY_TYPE,
  DISPLAY_PRICE,
  FETCH_FACETS,
  FETCH_SUGGESTED_FACETS,
  INVENTORY_TYPE,
  LATITUDE,
  LONGITUDE,
  MAKE,
  MODEL,
  PAGE_NUMBER,
  PAGE_SIZE,
  RADIUS,
  SEO,
  SORT_BY,
  STATE_CODE,
  ZIP_CODE,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import { SRP_BASE_PATHNAME } from 'site-modules/shared/constants/inventory/routes-constants';

export const SHOP_5016_CAMPAIGN = 'shop-5016-backfill';
export const SHOP_5016_PAGE_SIZE = 21;
export const SHOP_5016_MAX_PRIMARY_COUNT = 5;
export const SHOP_5016_API_PATH = '/purchasefunnel/v1/srp/inventory';

export const SHOP_5016_STRATEGIES = {
  RADIUS: 'chal1',
  SIMILAR: 'chal2',
};

export const SHOP_5016_TRACKING = {
  [SHOP_5016_STRATEGIES.RADIUS]: {
    widget: 'extended-radius',
    card: 'extended-radius-vehicle-card-list',
  },
  [SHOP_5016_STRATEGIES.SIMILAR]: {
    widget: 'related-matches',
    card: 'related-matches-vehicle-card-list',
  },
};

const RADIUS_EXPANSION = {
  10: 100,
  25: 100,
  50: 200,
  75: 200,
  100: 500,
  200: 500,
};

const SIMILAR_MODEL_FILTERS = [
  INVENTORY_TYPE,
  'year',
  'mileage',
  'exteriorColor',
  'transmission',
  'engineType',
  'driveTrain',
  'cylinders',
  'bedLength',
  'totalSeating',
  'feature',
  'truckCabSize',
  'displacement',
  RADIUS,
  DISPLAY_PRICE,
  ZIP_CODE,
  STATE_CODE,
  LATITUDE,
  LONGITUDE,
];

const getFirstValue = value => (Array.isArray(value) ? value[0] : value);

const toNumber = value => {
  const numericValue = Number(getFirstValue(value));
  return Number.isFinite(numericValue) ? numericValue : null;
};

export function getExpandedRadius(radius) {
  const numericRadius = toNumber(radius);
  return numericRadius === null ? null : RADIUS_EXPANSION[numericRadius] || null;
}

export function getLocationParams(visitorLocation = {}) {
  return pickBy(
    {
      [ZIP_CODE]: visitorLocation.zipCode,
      [STATE_CODE]: visitorLocation.stateCode,
      [LATITUDE]: visitorLocation.latitude,
      [LONGITUDE]: visitorLocation.longitude,
      dma: visitorLocation.dma,
    },
    value => value !== undefined && value !== null && value !== ''
  );
}

function getBaseParams({ selectedFacets = {}, visitorLocation = {} }) {
  return {
    ...omit(selectedFacets, [PAGE_NUMBER, PAGE_SIZE, SORT_BY, 'sortByFieldsWithWeights', 'national', 'rooftopId']),
    ...getLocationParams(visitorLocation),
    [PAGE_NUMBER]: 1,
    [PAGE_SIZE]: SHOP_5016_PAGE_SIZE,
    [FETCH_FACETS]: false,
    [FETCH_SUGGESTED_FACETS]: false,
    [SEO]: false,
  };
}

export function buildBackfillUrl(params) {
  const query = getSearchResultsQuery(params);
  return `${SHOP_5016_API_PATH}?${query}`;
}

export function isShop5016Eligible({ pathName, isNational = false, isDealerSrp = false, selectedFacets = {} } = {}) {
  const publicationState = getPagePublicationState(selectedFacets[INVENTORY_TYPE] || []);

  return (
    pathName === SRP_BASE_PATHNAME && !isNational && !isDealerSrp && ['used', 'new_used'].includes(publicationState)
  );
}

export function buildRadiusExpansionParams({ selectedFacets = {}, visitorLocation = {}, radius } = {}) {
  const expandedRadius = getExpandedRadius(radius || selectedFacets[RADIUS]);
  if (!expandedRadius) return null;

  return {
    ...getBaseParams({ selectedFacets, visitorLocation }),
    [RADIUS]: expandedRadius,
  };
}

export function expandDisplayPrice(displayPrice) {
  const range = getFirstValue(displayPrice);
  if (!range || typeof range !== 'string') return undefined;

  const [minimum, maximum] = range.split('-');
  const expandBound = (bound, direction) => {
    if (!bound || bound === '*') return '*';
    const numericBound = Number(bound);
    if (!Number.isFinite(numericBound)) return '*';
    return Math.max(0, numericBound + direction * 10000);
  };

  return `${expandBound(minimum, -1)}-${expandBound(maximum, 1)}`;
}

export function getPrimaryBodyType(primaryInventory = []) {
  if (!primaryInventory.length) return undefined;
  return get(primaryInventory[0], 'vehicleInfo.styleInfo.bodyType') || get(primaryInventory[0], 'bodyType');
}

export function buildSimilarModelsParams({ selectedFacets = {}, visitorLocation = {}, primaryInventory = [] } = {}) {
  const params = {
    ...pick(selectedFacets, SIMILAR_MODEL_FILTERS),
    ...getLocationParams(visitorLocation),
    [PAGE_NUMBER]: 1,
    [PAGE_SIZE]: SHOP_5016_PAGE_SIZE,
    [FETCH_FACETS]: false,
    [FETCH_SUGGESTED_FACETS]: false,
    [SEO]: false,
  };

  const expandedPrice = expandDisplayPrice(selectedFacets[DISPLAY_PRICE]);
  if (expandedPrice) params[DISPLAY_PRICE] = expandedPrice;
  else delete params[DISPLAY_PRICE];

  const bodyType = getPrimaryBodyType(primaryInventory);
  if (bodyType) params[BODY_TYPE] = bodyType;
  else delete params[BODY_TYPE];

  delete params[MAKE];
  delete params[MODEL];
  delete params[SORT_BY];

  return params;
}

export function getBackfillResults(response) {
  return get(response, 'inventories.results', []);
}

export function getUniqueBackfillVehicles({ primaryInventory = [], response, maxResults = SHOP_5016_PAGE_SIZE } = {}) {
  const availableSlots = Math.max(0, maxResults - primaryInventory.length);
  const primaryVins = new Set(primaryInventory.map(vehicle => vehicle?.vin).filter(Boolean));
  const seenVins = new Set(primaryVins);

  return getBackfillResults(response).filter(vehicle => {
    const { vin } = vehicle;

    if (seenVins.has(vin) || seenVins.size - primaryVins.size >= availableSlots) return false;

    seenVins.add(vin);
    return true;
  });
}
